import { computed, inject } from '@angular/core';
import { Account, AccountPairSettingsOverrideControllerService, Pair } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { getBuySellCommissionRates } from '@shared/utils/rates';
import { isDefined } from '@stillmandigital/common';
import { AccountPairOverrideViewModel } from 'app/accounts/store/routes/routes.interfaces';
import { toAccountPairOverrideViewModel } from 'app/accounts/store/routes/routes.utils';
import { pipe, switchMap } from 'rxjs';

export const useCustomCommissions = () =>
  signalStoreFeature(
    {},
    withProps(() => ({
      _customCommissionsService: inject(AccountPairSettingsOverrideControllerService),
    })),
    withState({
      customCommissions: {} as Record<number, AccountPairOverrideViewModel[]>,
    }),
    withComputed(store => {
      const selectCustomCommissionOverrideByIds = computed(
        () => (accountId?: number, basePairId?: number) =>
          isDefined(accountId) && isDefined(basePairId)
            ? store.customCommissions()[accountId]?.find(ccr => ccr.pairId === basePairId)
            : undefined,
      );

      const selectBuySellCommissionRatesByParams = computed(() => (account?: Account, basePair?: Pair) => {
        const customOverride = selectCustomCommissionOverrideByIds()(account?.id, basePair?.id);

        return getBuySellCommissionRates(account, basePair, customOverride);
      });
      return {
        selectCustomCommissionOverrideByIds,
        selectBuySellCommissionRatesByParams,
      };
    }),
    withMethods(store => {
      const loadCustomCommissions = rxMethod<number>(
        pipe(
          switchMap(accountId =>
            store._customCommissionsService
              .getAccountPairOverrides({
                accountId,
              })
              .pipe(
                tapResponse({
                  next: customCommissions => {
                    patchState(store, {
                      customCommissions: {
                        ...store.customCommissions(),
                        [accountId]: customCommissions.map(cc => toAccountPairOverrideViewModel(cc)),
                      },
                    });
                  },
                  error: error => {
                    console.error('Failed to load custom commissions', error);
                  },
                }),
              ),
          ),
        ),
      );

      return {
        loadCustomCommissions,
      };
    }),
  );
